/**
 * Eligibility Text Normalizer
 * 
 * DISPLAY-ONLY utility for normalizing eligibility wording.
 * Does NOT modify source data - only transforms text for display.
 * 
 * Goals:
 * - Clear, plain English
 * - Student-first language
 * - Consistent tone across views
 * - Preserve factual meaning exactly
 */

/**
 * Normalize eligibility text for display
 * @param {string} text - Raw eligibility text from data
 * @returns {string} - Normalized text for display
 */
export const normalizeEligibility = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let normalized = text.trim();
  
  // Remove excessive whitespace
  normalized = normalized.replace(/\s+/g, ' ');
  
  // Normalize common awkward phrases to student-first language
  const replacements = [
    // "Applicants must be..." -> "Open to students who are..."
    [/^Applicants?\s+must\s+be\s+/i, 'Open to students who are '],
    [/^Applicant\s+must\s+/i, 'Students must '],
    [/^Applicants?\s+must\s+/i, 'Students must '],
    
    // "Applicant may be..." -> "Open to students who are..."
    [/^Applicants?\s+may\s+be\s+/i, 'Open to students who are '],
    
    // "Candidate must..." -> "Students must..."
    [/^Candidates?\s+must\s+/i, 'Students must '],
    
    // "Individual must..." -> "Students must..."
    [/^Individuals?\s+must\s+/i, 'Students must '],
    
    // "Award is open to..." -> "Open to..."
    [/^(?:This\s+)?Award\s+is\s+open\s+to\s+/i, 'Open to '],
    [/^(?:This\s+)?Scholarship\s+is\s+open\s+to\s+/i, 'Open to '],
    [/^(?:This\s+)?Grant\s+is\s+open\s+to\s+/i, 'Open to '],
    
    // "Requirements include..." -> "Requirements:"
    [/^Requirements?\s+include[s]?[:\s]+/i, 'Requirements: '],
    
    // "Eligible students include..." -> "Open to..."
    [/^Eligible\s+students?\s+include[s]?[:\s]+/i, 'Open to '],
    
    // "To be eligible..." -> "Eligibility:"
    [/^To\s+be\s+eligible[,:\s]+/i, 'Eligibility: '],
    
    // "Eligibility requirements:" -> "Requirements:"
    [/^Eligibility\s+requirements?[:\s]+/i, 'Requirements: '],
    
    // "Must be a..." -> "Open to..."
    [/^Must\s+be\s+(?:a\s+)?/i, 'Open to '],
    
    // "Student must be..." -> "Students must be..."
    [/^Student\s+must\s+/i, 'Students must '],
    
    // "Recipient must..." -> "Students must..."
    [/^Recipients?\s+must\s+/i, 'Students must '],
  ];
  
  for (const [pattern, replacement] of replacements) {
    if (pattern.test(normalized)) {
      normalized = normalized.replace(pattern, replacement);
      break; // Only apply first matching replacement
    }
  }
  
  // Clean up punctuation
  // Remove double periods
  normalized = normalized.replace(/\.{2,}/g, '.');
  
  // Remove trailing semicolons and replace with period
  normalized = normalized.replace(/;\s*$/, '.');
  
  // Ensure ends with proper punctuation if it's a complete sentence
  if (normalized.length > 20 && !/[.!?]$/.test(normalized)) {
    normalized = normalized + '.';
  }
  
  // Capitalize first letter
  if (normalized.length > 0) {
    normalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
  
  return normalized;
};

/**
 * Normalize eligibility bullets for display
 * @param {string[]} bullets - Array of eligibility bullet points
 * @returns {string[]} - Normalized bullets
 */
export const normalizeEligibilityBullets = (bullets) => {
  if (!bullets || !Array.isArray(bullets)) return bullets;
  
  return bullets
    .map(bullet => normalizeEligibility(bullet))
    .filter(bullet => bullet && bullet.length > 0);
};

/**
 * Derive clean eligibility bullets from raw text
 * @param {string} text - Raw eligibility text
 * @param {number} maxBullets - Maximum number of bullets to return
 * @returns {string[]|null} - Array of normalized bullets or null
 */
export const deriveEligibilityBullets = (text, maxBullets = 3) => {
  if (!text || typeof text !== 'string') return null;
  
  // Split by common delimiters
  const sentences = text.split(/[;|]/g)
    .map(s => s.trim())
    .filter(s => s.length > 10 && s.length < 150);
  
  if (sentences.length >= 2) {
    return sentences
      .slice(0, maxBullets)
      .map(s => normalizeEligibility(s));
  }
  
  // Try splitting by periods if semicolons didn't work
  const periodSplit = text.split(/\.\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 10 && s.length < 150);
  
  if (periodSplit.length >= 2) {
    return periodSplit
      .slice(0, maxBullets)
      .map(s => normalizeEligibility(s));
  }
  
  return null;
};

export default normalizeEligibility;
